import React, { useCallback, useContext } from 'react'

import { useEditor } from '@craftjs/core'

import Row from './Row'

import {
  ILayout,
  ILayout_Row,
  ILayout_Cell,
  ILayout_Options,
} from '@kaidam/fc-shared/src/types/layout'

import {
  LAYOUT_NODE_TYPE_ROW,
  LAYOUT_NODE_TYPE_ROW_INNER,
  LAYOUT_NODE_TYPE_CELL,
  LAYOUT_CELL_TYPE_EMPTY,
} from '@kaidam/fc-shared/src/constants'

import EditorContext from './EditorContext'

const useEditorActions = () => {
  const {
    actions,
    query,
  } = useEditor()

  const editorContext = useContext(EditorContext)

  // reset selection
  const deselect = useCallback(() => {
    actions.selectNode()
  }, [
    actions.selectNode,
  ])

  const select = useCallback((id: string) => {
    actions.selectNode(id)
  }, [
    actions.selectNode,
  ])

  const RowAny = Row as any

  const exportLayout = useCallback((options: ILayout_Options): ILayout => {
    const exportCell = (id: string): ILayout_Row | ILayout_Cell => {
      const node = query.getState().nodes[id]
      if(node.data.displayName == LAYOUT_NODE_TYPE_ROW) {
        const innerNode = query.getState().nodes[node.data.linkedNodes.rowinner]
        const rowConfig = node.data.props.config || {}
        const row: ILayout_Row = {
          config: rowConfig,
          cells: innerNode.data.nodes.map(exportCell),
        }
        return row
      }
      else {
        const config = node.data.props.config || {}
        const data = node.data.props.data || {}
        const type = node.data.props.type
        const cell: ILayout_Cell = {
          config,
          data,
          type,
        }
        return cell
      }
    }
    
    const exportRow = (id: string): ILayout_Row => {
      return exportCell(id) as ILayout_Row
    }

    const rows = query
      .node('ROOT')
      .descendants()
      .filter(id => {
        const node = query.getState().nodes[id]
        return node.data.displayName == LAYOUT_NODE_TYPE_ROW
      })
      .map(exportRow)
    return {
      options,
      rows,
    }
  }, [])

  const moveRow = useCallback((id: string, direction: 1 | -1) => {
    const rows = query
      .node('ROOT')
      .descendants()
    if(direction < 0) {
      const sourceIndex = rows.findIndex(rootRowId => rootRowId == id)
      const targetIndex = sourceIndex - 1
      if(targetIndex < 0) return
      if(targetIndex >= rows.length) return
      actions.move(id, 'ROOT', targetIndex)
    }
    else {
      const sourceIndex = rows.findIndex(rootRowId => rootRowId == id)
      if(sourceIndex >= rows.length -1) return
      const sourceId = rows[sourceIndex + 1]
      actions.move(sourceId, 'ROOT', sourceIndex)
    }
    
  }, [])

  // remove any rows that have no children
  const removeEmptyRows = useCallback(() => {

    query
      .node('ROOT')
      .descendants()
      .filter(id => {
        const node = query.getState().nodes[id]
        return node.data.displayName == LAYOUT_NODE_TYPE_ROW
      })
      .filter(id => {
        const remainingDescendants = query
          .node(id)
          .descendants(true)
          .filter(childId => {
            const node = query.getState().nodes[childId]
            if(node.data.displayName == LAYOUT_NODE_TYPE_ROW) return false
            if(node.data.custom.type == LAYOUT_NODE_TYPE_ROW_INNER) return false
            return true
          })
        return remainingDescendants.length > 0 ? false : true
      })
      .forEach(id => {
        actions.delete(id)
      })
  }, [])

  const processEmptyCell = useCallback(() => {
    let emptyCellId = ''
    let otherCellCount = 0
    query
      .node('ROOT')
      .descendants(true)
      .filter(id => {
        const node = query.getState().nodes[id]
        return node.data.displayName == LAYOUT_NODE_TYPE_ROW
      })
      .forEach(id => {
        query
          .node(id)
          .descendants(true)
          .forEach(childId => {
            const node = query.getState().nodes[childId]
            if(node.data.displayName == LAYOUT_NODE_TYPE_ROW) return
            if(node.data.custom.type == LAYOUT_NODE_TYPE_ROW_INNER) return
            if(node.data.props.type == LAYOUT_CELL_TYPE_EMPTY) emptyCellId = childId
            else otherCellCount++
          })
      })

    // we need to inject an empty cell because there are no other cells
    if(otherCellCount <= 0 && !emptyCellId) {
      const rowNodeTree = query.parseReactElement(
        <RowAny
          row={{
            cells: [{
              type: LAYOUT_CELL_TYPE_EMPTY,
              data: {},
            }]
          }}
          options={ editorContext.options }
        />
      ).toNodeTree()
      actions.addNodeTree(rowNodeTree, 'ROOT', 0)
    }
    // we need to remove the empty cell because there are other cells
    else if(otherCellCount > 0 && emptyCellId) {
      actions.delete(emptyCellId)
    }
  }, [
    editorContext.options,
  ])

  const processNewCells = useCallback(() => {
    query
      .node('ROOT')
      .descendants(true)
      .filter(id => {
        const node = query.getState().nodes[id]
        return node.data.displayName == LAYOUT_NODE_TYPE_CELL && node.data.props.isNew
      })
      .forEach(id => {
        actions.setProp(id, props => props.isNew = false)
        actions.selectNode(id)
        setTimeout(() => {
          editorContext.setEditorEvent({id, action: 'edit'})
        }, 100)
      })
  }, [])


  // move any cells floating on the top level into their own row
  const hoistFloatingCells = useCallback(() => {
    const rootChildren = query
      .node('ROOT')
      .descendants()

    rootChildren
      .filter(id => {
        const node = query.getState().nodes[id]
        return node.data.displayName != LAYOUT_NODE_TYPE_ROW
      })
      .forEach(id => {
        const insertIndex = rootChildren.indexOf(id)
        const floatingNodeTree = query.node(id).toSerializedNode()
        const cellProps = floatingNodeTree.props
        actions.delete(id)
        const rowNodeTree = query.parseReactElement(
          <RowAny
            row={{
              cells: [cellProps]
            }}
            options={ editorContext.options }
          />
        ).toNodeTree()
        actions.addNodeTree(rowNodeTree, 'ROOT', insertIndex)
      })
  }, [
    editorContext.options,
  ])

  const deleteNode = useCallback((id: string) => {
    if(!id) return
    deselect()
    actions.delete(id)
    setTimeout(() => {
      removeEmptyRows()
      processEmptyCell()
    }, 1)
  }, [])

  const processChangeEvent = useCallback(() => {
    hoistFloatingCells()
    setTimeout(() => {
      processEmptyCell()
      removeEmptyRows()
      processNewCells()
    }, 1)
  }, [])

  const toggleLocked = useCallback((id: string) => {
    const node = query.getState().nodes[id]
    const newValue = node.data.props.config?.locked ?
      false :
      true
    actions.setProp(id, props => {
      props.config = Object.assign({}, props.config, {
        locked: newValue,
      })
    })
    if(node.data.displayName == LAYOUT_NODE_TYPE_ROW) {
      query
        .node(id)
        .descendants(true)
        .filter(id => {
          const node = query.getState().nodes[id]
          return node.data.displayName == LAYOUT_NODE_TYPE_CELL
        })
        .forEach(id => {
          actions.setProp(id, props => {
            props.config = Object.assign({}, props.config, {
              locked: newValue,
            })
          })
        })
    }
  }, [])

  return {
    deselect,
    select,
    moveRow,
    exportLayout,
    processEmptyCell,
    removeEmptyRows,
    hoistFloatingCells,
    deleteNode,
    processChangeEvent,
    toggleLocked,
  }
}

export default useEditorActions
